import { Subject } from 'rxjs';
import { Entity, YSubscription } from 'src/extensions';
import { Util } from 'src/util/util';
import { Doc, encodeStateAsUpdateV2 } from 'yjs';
import { Map as YMap, YMapEvent, Text as YText, Array as YArray, Transaction, YEvent, UndoManager } from 'yjs';
import deepEqual from 'deep-equal'

export class YDocument {
    constructor(private document: Doc) { }

    public transact<T>(f: (arg0: Transaction) => T, origin?: any) {
        this.document.transact(f, origin);
    }

    public getStateAsUpdate() {
        return encodeStateAsUpdateV2(this.document);
    }

    public getArray<T>(name: string): YArrayExtended<T> {
        const array = this.document.getArray<T>(name);
        return new YArrayExtended(array);
    }

    public getMap<T>(name: string): YMapExtended<T> {
        const map = this.document.getMap<T>(name);
        return new YMapExtended(map);
    }

    public getEntity<T extends Entity>(name: string, _default?: T): YEntity<T> {
        const map = this.document.getMap<any>(name);
        return new YEntity<T>(map, _default);
    }

    public getDictionary<T extends Entity>(name: string): YDictionary<T> {
        const map = this.document.getMap<any>(name);
        return new YDictionary<T>(map);
    }
}

export class YArrayExtended<T> {
    constructor(private array: YArray<T>) { }

    public toArray = () => this.array.toArray();
    public insert = (index: number, content: T[]) => this.array.insert(index, content);
    public delete = (index: number, length?: number) => this.array.delete(index, length);
    public getLength = () => this.array.length;

    public watch(func: (entity: T[]) => void): YSubscription {
        const event = new Subject<void>()
        const action = () => {
            func(this.array.toArray());
            event.next();
        };
        this.array.observe(action);
        setTimeout(action);  /* Always emit the initial value */
        return { unsubscribe: () => this.array.unobserve(action), event };
    }
}


export class YMapExtended<T> {
    constructor(private map: YMap<T>) { }

    public get = (key: string) => this.map.get(key);
    public set = (key: string, value: T) => this.map.set(key, value);
    public delete = (key: string) => this.map.delete(key);
    public clear = () => this.map.clear();
    public values = () => this.map.values();
    public keys = () => this.map.keys();
    public forEach = (f: (arg0: T, arg1: string, arg2: YMap<T>) => void) => this.map.forEach(f);
    public observe = (f: (arg0: YMapEvent<T>, arg1: Transaction) => void) => this.map.observe(f);
    public observeDeep = (f: (arg0: Array<YEvent<any>>, arg1: Transaction) => void) => this.map.observeDeep(f);
    public toJSON = () => this.map.toJSON();

    public getOrCreate(key: string, factory: () => T): T {
        const existing = this.map.get(key);
        if (existing !== undefined) {
            return existing;
        }
        const created = factory();
        this.map.set(key, created);
        return created;
    }

    public asArray(): T[] {
        return Array.from(this.map.values());
    }

    public watchAll(func: (keys: string[]) => void): YSubscription {
        const event = new Subject<void>()
        const action = () => {
            func(Array.from(this.map.keys()));
            event.next();
        };

        this.map.observeDeep(action);
        action(); /* Always emit the initial value */
        return {
            event,
            unsubscribe: () => {
                this.map.unobserveDeep(action);
                event.complete();
            }
        };
    }
}


export class YEntity<T extends Entity> {
    constructor(public map: YMap<any>, _default?: T) {
        if (Object.keys(map.toJSON()).length == 0 && _default != null) {
            this.set(_default);
        }
    }

    public clear = () => this.map.clear();

    public get(): T {
        return Util.clone(this.map.toJSON()) as T;
    }

    public set(entity: T) {
        this.map.doc?.transact(() => {
            for (let key of Object.keys(entity)) {
                const newvalue = entity[key];
                const oldvalue = this.map.get(key);
                if (!deepEqual(oldvalue, newvalue)) {
                    this.map.set(key, newvalue);
                }
            }
        });
    }

    /* NB Pushing to an array is not a modification and will cause desync */
    public modify(func: (entity: T) => void) {
        this.map.doc?.transact(() => {
            const entity = this.get();
            func(entity);
            for (let key of Object.keys(entity)) {
                const newvalue = entity[key];
                const oldvalue = this.map.get(key);
                if (!deepEqual(oldvalue, newvalue)) {
                    this.map.set(key, newvalue);
                }
            }
        });
    }

    public watch(func: (entity: T) => void): YSubscription {
        const event = new Subject<void>()
        const action = () => {
            func(this.get());
            event.next();
        };
        this.map.observeDeep(action);
        setTimeout(() => {
            func(this.get()); /* Always emit the initial value */
            event.next();
        });
        return {
            event,
            unsubscribe: () => {
                this.map.unobserveDeep(action);
                event.complete();
            }
        };
    }
}

export class YDictionary<T extends Entity> {
    constructor(private map: YMap<YMap<any>>) { }

    public length = () => this.map.size;
    public toJSON = () => this.map.toJSON() as { [key: string]: T };
    public clear = () => this.map.clear();
    public delete = (key: string) => this.map.delete(key);
    public observe = (f: (arg0: YMapEvent<YMap<any>>, arg1: Transaction) => void) => this.map.observe(f);
    public observeDeep = (f: (arg0: YEvent<any>[], arg1: Transaction) => void) => this.map.observeDeep(f);

    public watch(func: (id: string, yAction: "update" | "add" | "delete") => void) {
        this.map.observeDeep((events) => {
            for (let event of events) {
                for (let key of event.changes.keys) {
                    const action = key[1].action;

                    const id = event.target.get('id') as string;
                    if (id == undefined) {
                        for (let id of Object.keys(event.target.toJSON())) {
                            func(id, action);
                        }
                    } else {
                        func(id, action);
                    }
                }
            }
        });
    }

    public createUndoManager(id: string) {
        return new UndoManager(this.map, { captureTimeout: 50, trackedOrigins: new Set([id]) });
    }

    public get(key: string): T {
        const internal = Util.clone(this.map.get(key)?.toJSON() ?? {});
        return internal as T;
    }

    private getEntity(key: string): YEntity<T> {
        let internal = this.map.get(key);
        if (internal == null) {
            const map = new YMap<any>();
            map.set('id', key)
            internal = this.map.set(key, map);
        }
        return new YEntity<T>(internal);
    }

    public set(key: string, value: T) {
        this.map.doc?.transact(() => {
            this.getEntity(key).set(value);
        });
    }

    public modify(key: string, func: (entity: T) => void): void {
        this.map.doc?.transact(() => {
            this.getEntity(key).modify(func);
        });
    }

    public watchAll(func: () => void): YSubscription {
        const event = new Subject<void>();
        const action = () => {
            func();
            event.next();
        };

        this.map.observeDeep(action);
        action(); /* Always emit the initial value */
        return {
            event,
            unsubscribe: () => {
                this.map.unobserveDeep(action);
                event.complete();
            }
        };
    }
}

export class WYText extends YText {
    public watch(func: (text: string) => void): YSubscription {
        const event = new Subject<void>()
        const action = () => {
            func(this.toString());
            event.next();
        };
        this.observe(action);
        setTimeout(action); /* Always emit the initial value */
        return {
            event,
            unsubscribe: () => {
                this.unobserve(action);
                event.complete();
            }
        };
    };
}
